import { TranslateService } from '@ngx-translate/core';
import { ConfigInstanceToken, ConfigInterface } from '../../interfaces/config.interface';
import * as _ from 'lodash';
import moment from 'moment';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { CONFIRMATION_TYPES } from '../../enums/confirmation-types.enum';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { ValueHelperInstanceToken } from '../../interfaces/value-helper.interface';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { isDefined } from '../../utilities/is-defined';
import { NotificationUtilities } from '../../utilities/notification-utilities';
import { TimeUtilities } from '../../utilities/time-utilities';
import { MaterialModel } from '../low-level/material.model';
import { CONSUME_CODE } from '../low-level/material.enum';
import { NotificationModel } from '../low-level/notification.model';
import { ValueHelperModel } from '../low-level/value-helper.model';
import { DurationModelOptions } from '../options/duration-model-options';
import { RadioModelOptions } from '../options/radio-model-options';
import { DateModel } from '../primitive/date.model';
import { DurationModel, JsonDurationModel } from '../primitive/duration.model';
import { MultilineTextModel, ODataTextLineOutModel } from '../primitive/multiline-text.model';
import { ODataTextOutModel, TextModel } from '../primitive/text.model';
import { TimeModel } from '../primitive/time.model';
import { TreeViewModel } from '../primitive/tree-view.model';
import { ArrayModel } from './array.model';
import { CodeModel } from './code.model';
import { CompositeModel } from './composite.model';
import { EquipmentStatus, JsonConfirmationEquipmentModel } from './confirmation-equipment.model';
import { CrmCodeModel, ODataCrmCodeOutModel } from './crm-code.model';
import { DropdownModel } from './dropdown.model';
import { RadioModel } from './radio.model';
import { BooleanModel } from '../primitive/boolean.model';
import { TimeModelOptions } from '../options/time-model-options';
import { UserMasterInstanceToken } from '../../interfaces/user-master.interface';
import ShortTextOptionsForInstallationPaperwork = ConfigConstants.INSTALLATION_PAPERWORK_FOLLOWUP_SHORT_TEXT;

export class ConfirmationFollowupModel extends CompositeModel {
    private readonly configService: ConfigInterface;

    public followupOptions: RadioModel<TextModel>;
    public appointmentDate: DateModel;
    public appointmentTime: TimeModel;
    public followUpUMRCodes: DropdownModel<TextModel> | undefined;
    public notificationType: ConfigConstants.NotificationTypes;
    public additionalTime: DurationModel;
    public shortCrmFollowupDateTime: moment.Moment;
    public isForced = false;
    public installationPaperworkShortText = ShortTextOptionsForInstallationPaperwork.FOLLOW_UP_EMPTY;
    public followUpReasons: DropdownModel<TextModel> | undefined;
    public followupTextLong: MultilineTextModel;
    public followupTextShort: MultilineTextModel;
    public additionalInfo: MultilineTextModel;
    public crmLongTextTRTESet: MultilineTextModel | undefined;
    public shortCrmFollowupOptions: string | undefined;
    public shortCrmAppointmentTime: moment.Moment | undefined;
    public normalCrmFollowupOptions: string;
    public normalCrmAppointmentTime: moment.Moment;
    public followUpText: string[] = [];
    public readonly assayIndicator = ConfigConstants.assayIndicator;
    public reasonsCode: TreeViewModel<CodeModel> | undefined;
    public reasonsCodeFallback: DropdownModel<TextModel>;
    public assayNumber: MultilineTextModel;
    public lotNumber: MultilineTextModel;
    public showLegalCheckHandover = false;
    public notificationLanguage: string;
    public legalCheckHandoverUserDocumentationText: string;
    public legalCheckHandoverSafetyInformationText: string;
    public legalCheckHandoverUserDocumentation: RadioModel<TextModel>;
    public legalCheckHandoverSafetyInformation: RadioModel<TextModel>;
    public startImmediately: DisplayDecorator<BooleanModel> | undefined;
    public remoteWork: DisplayDecorator<BooleanModel>;
    public remoteWorkValue = false;
    public isAppBookingEnabled = false;
    public appBookingOptions: RadioModel<TextModel>;
    public isAppointmentConfirmed = false;
    public appBookingDuration: number;
    public isDedicatedResourceEnabled = false;
    public defaultDedicatedResource = false;
    public medPhysicsEvaluation: RadioModel<TextModel> | undefined;
    public showMedPhysicsEvaluation = false;
    private medPhysicsEvaluationCodes: ValueHelperModel[] = [];
    private readonly notificationUtilities: NotificationUtilities;

    /**
     * Prepare a model for the Confirmation Followup Model.
     *
     * @param notification notification model
     * @param notificationType type of notification
     * @param confirmationType type of confirmation
     * @param isMaterialRequestDependent if the followup is dependent on material request
     * @param equipmentStatus status of the equipment after fix
     * @param materialsList list of materials
     * @class
     */
    public constructor(
        public notification: NotificationModel,
        notificationType: ConfigConstants.NotificationTypes,
        public confirmationType: CONFIRMATION_TYPES,
        public isMaterialRequestDependent: boolean,
        public equipmentStatus: EquipmentStatus | undefined,
        public materialsList: MaterialModel[] | undefined,
    ) {
        super('ConfirmationFollowupModel');
        const injector = CustomInjector.getInjector();
        this.configService = injector.get(ConfigInstanceToken);
        const valueHelperService = injector.get(ValueHelperInstanceToken);
        const translate = injector.get(TranslateService);
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        const notificationUtilities = injector.get(NotificationUtilities);
        this.notificationUtilities = notificationUtilities;

        this.notificationType = notificationType;
        const followUpReasons = valueHelperService.getFollowupReason();
        if (followUpReasons && followUpReasons.length > 0) {
            const followUpReasonsEntries = followUpReasons.map(
                (vh: ValueHelperModel) =>
                    new DisplayDecorator<TextModel>(vh.descText, new TextModel('FollowupReason', vh.descText)),
            );

            this.followUpReasons = <DropdownModel<TextModel>>(
                this.set(new DropdownModel<TextModel>('FollowupReason', followUpReasonsEntries))
            );
            modelOptionsLoader.load(this.followUpReasons, 'ConfirmationFollowup', 'FollowupReason');
            const selectDefaultFollowupReason = this.configService.getSelectDefaultFollowupReason();
            if (selectDefaultFollowupReason) {
                // sele ct the first entry
                this.followUpReasons.selectByIndex(0);
            }
        }

        // show followup UMR codes only for notification type MU
        if (this.notificationType === ConfigConstants.NotificationTypes.Update) {
            const followUpUMRCodes = valueHelperService.getFollowUpUMRCode();

            const followUpUMRCodesEntries = followUpUMRCodes.map(
                (vh: ValueHelperModel) =>
                    new DisplayDecorator<TextModel>(vh.descText, new TextModel('UmrCode', vh.code)),
            );

            this.followUpUMRCodes = <DropdownModel<TextModel>>(
                this.set(new DropdownModel<TextModel>('UmrCode', followUpUMRCodesEntries))
            );
            modelOptionsLoader.load(this.followUpUMRCodes, 'ConfirmationFollowup', 'UMRCode');
        }

        const optionGroup: Array<DisplayDecorator<TextModel>> = [];
        optionGroup.push(
            new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_FOLLOWUP_OPTION_YES'),
                new TextModel(ConfigConstants.optionYes, ConfigConstants.optionValueYes),
            ),
        );
        optionGroup.push(
            new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_FOLLOWUP_OPTION_NO'),
                new TextModel(ConfigConstants.optionNo, ConfigConstants.optionValueNo),
            ),
        );
        optionGroup.push(
            new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_FOLLOWUP_OPTION_NA'),
                new TextModel(ConfigConstants.optionNA, ConfigConstants.optionValueNA),
            ),
        );

        this.followupOptions = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'FollowUpVisitFlag',
                    optionGroup,
                    new RadioModelOptions({ initialSelection: 2, invalidSelection: 2 }),
                ),
            )
        );

        const nextWorkingDateAndTime = this.updateNextWorkingDayDateTime();
        this.appointmentTime = <TimeModel>(
            this.set(
                new TimeModel(
                    'FollowUpVisitTime',
                    nextWorkingDateAndTime,
                    new TimeModelOptions({ minValue: moment().startOf('minutes') }),
                ),
            )
        );
        this.appointmentDate = <DateModel>this.set(new DateModel('FollowUpVisitDate', this.appointmentTime.value));

        const followupDefaultAdditionalTime = this.configService.getFollowupDefaultAdditionalTime();
        const followupMinAdditionalTime = this.configService.getFollowupMinAdditionalTime();
        const followupAdditionalTimeStep = this.configService.getFollowupAdditionalTimeStep();

        this.additionalTime = <DurationModel>this.set(
            new DurationModel(
                'FollowUpVisitAddTime',
                moment.duration(followupDefaultAdditionalTime, 'minutes'),
                new DurationModelOptions({
                    step: followupAdditionalTimeStep,
                    unit: <moment.unitOfTime.Base>'minutes',
                    minValue: moment.duration(followupMinAdditionalTime, 'minutes'),
                }),
            ),
        );

        // For  short confirmation, set appointment date to next working day and appointment time as received from CODGRP table via ValueHelperService
        // Saud i : does not have value helper and date is +3 advance
        const followUpDispatchTimeValueHelper = valueHelperService.getFollowDispatchTime()[0];

        if (this.configService.getShowStartImmediatelyFollowUp()) {
            this.startImmediately = new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_FOLLOWUP_START_IMMEDIATELY'),
                new BooleanModel('StartImmediately', false),
            );
            modelOptionsLoader.load(this.startImmediately, 'ConfirmationFollowup', 'StartImmediately');
        }

        if (this.configService.getShowRemoteWork()) {
            this.remoteWork = new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_FOLLOWUP_REMOTE_WORK'),
                new BooleanModel('RemoteWork', false),
            );
            modelOptionsLoader.load(this.remoteWork, 'ConfirmationFollowup', 'RemoteWork');
        }

        if (this.configService.showFollowupLongText()) {
            this.followupTextLong = new MultilineTextModel('FollowupText');
            modelOptionsLoader.load(this.followupTextLong, 'ConfirmationFollowup', 'FollowupText');
            this.followupTextLong.options = {
                ...this.followupTextLong.options,
                readonly: true,
            };

            this.followupTextShort = <MultilineTextModel>this.set(new MultilineTextModel('CrmLongTextSFOLSet'));
        }
        if (this.configService.showFollowupAdditionalInfo()) {
            this.additionalInfo = new MultilineTextModel('AdditionalInfo');
            modelOptionsLoader.load(this.additionalInfo, 'ConfirmationFollowup', 'FollowupText');
        }
        if (this.configService.showFollowupLongText() || this.configService.showFollowupAdditionalInfo()) {
            this.crmLongTextTRTESet = <MultilineTextModel>this.set(new MultilineTextModel('CrmLongTextTRTESet', ''));
        }
        if (this.isMaterialRequestDependent) {
            this.setFollowUpDependency();
        }
        if (this.confirmationType === CONFIRMATION_TYPES.SHORT) {
            this.shortCrmFollowupDateTime = followUpDispatchTimeValueHelper
                ? moment(followUpDispatchTimeValueHelper.code, ConfigConstants.formatNumberPatternTime)
                : nextWorkingDateAndTime;
            this.setShortConfirmationDependency();
        }
        if (this.configService.getDisplayTrendCodeInFollowup()) {
            this.reasonsCode = notificationUtilities.getEquipmentErrorCodes(notification);
            if (this.reasonsCode) {
                modelOptionsLoader.load(this.reasonsCode, 'Equipment', 'Reason');
            }
            this.assayNumber = new MultilineTextModel('CrmLongTextASSASet');
            modelOptionsLoader.load(this.assayNumber, 'Equipment', 'AssayNumber');

            this.assayNumber.options = {
                ...this.assayNumber.options,
                applicationIdentifier: ConfigConstants.ApplicationIdentifier.ProductIdentifier,
            };
            this.lotNumber = new MultilineTextModel('CrmLongTextLOTNSet');
            modelOptionsLoader.load(this.lotNumber, 'Equipment', 'LotNumber');
            this.lotNumber.options = {
                ...this.lotNumber.options,
                applicationIdentifier: ConfigConstants.ApplicationIdentifier.LotNumber,
            };
        }

        this.showLegalCheckHandover =
            this.confirmationType === CONFIRMATION_TYPES.NORMAL &&
            this.configService.getLegalCheckHandoverSafetyInformationCode().length > 0 &&
            notificationUtilities.isInstallationNotificationWithIHOT(notification);
        if (this.showLegalCheckHandover) {
            this.notificationLanguage = notification.languageId;
            this.legalCheckHandoverSafetyInformationText =
                this.configService.getLegalCheckHandoverSafetyInformationText();
            const legalCheckHandoverSafetyInformationCode =
                this.configService.getLegalCheckHandoverSafetyInformationCode();
            this.legalCheckHandoverUserDocumentationText =
                this.configService.getLegalCheckHandoverUserDocumentationText();
            const legalCheckHandoverUserDocumentationCode =
                this.configService.getLegalCheckHandoverUserDocumentationCode();
            this.legalCheckHandoverSafetyInformation = new RadioModel<TextModel>(
                legalCheckHandoverSafetyInformationCode,
                optionGroup,
                new RadioModelOptions({ initialSelection: 2, invalidSelection: 2 }),
            );
            this.legalCheckHandoverUserDocumentation = new RadioModel<TextModel>(
                legalCheckHandoverUserDocumentationCode,
                optionGroup,
                new RadioModelOptions({ initialSelection: 2, invalidSelection: 2 }),
            );
        }

        const userMasterService = injector.get(UserMasterInstanceToken);
        this.isAppBookingEnabled = userMasterService.isPaceOnlineUser()
            ? this.configService.getAppBookingForPartners()
            : this.configService.getAppBookingEnabled();
        if (this.isAppBookingEnabled) {
            const appBookingOptionGroup: Array<DisplayDecorator<TextModel>> = [];
            appBookingOptionGroup.push(
                new DisplayDecorator(
                    <string>translate.instant('CONFIRMATION_FOLLOWUP_TYPE_WITH_APPOINTMENT_LABEL'),
                    new TextModel(ConfigConstants.optionYes, ConfigConstants.optionValueYes),
                ),
            );
            appBookingOptionGroup.push(
                new DisplayDecorator(
                    <string>translate.instant('CONFIRMATION_FOLLOWUP_TYPE_WITHOUT_APPOINTMENT_LABEL'),
                    new TextModel(ConfigConstants.optionNo, ConfigConstants.optionValueNo),
                ),
            );
            this.appBookingOptions = new RadioModel<TextModel>(
                'AppBookingOptions',
                appBookingOptionGroup,
                new RadioModelOptions({ initialSelection: -1, invalidSelection: -1, required: true }),
            );

            this.isDedicatedResourceEnabled = this.configService.getAppBookingShowDedicated();
            this.defaultDedicatedResource = this.configService.getAppBookingDefaultDedicated();
            this.appBookingDuration = this.configService.getFollowupMinAdditionalTime();
        }

        this.initializeMedPhysicsEvaluation(valueHelperService.getTexasEpeCodes(), translate);
    }

    private initializeMedPhysicsEvaluation(texasEpeCodes: ValueHelperModel[], translate: TranslateService): void {
        const yesCode = texasEpeCodes.find((code) => code.code === ConfigConstants.TEXAS_EPE_CODE.YES);
        const noCode = texasEpeCodes.find((code) => code.code === ConfigConstants.TEXAS_EPE_CODE.NO);
        // Without both answers from the backend there is nothing valid to send, so the question is never asked
        if (this.confirmationType !== CONFIRMATION_TYPES.NORMAL || !isDefined(yesCode) || !isDefined(noCode)) {
            return;
        }

        this.medPhysicsEvaluationCodes = [yesCode, noCode];
        this.medPhysicsEvaluation = new RadioModel<TextModel>(
            'MedPhysicsEvaluation',
            [
                new DisplayDecorator(
                    <string>translate.instant('CONFIRMATION_FOLLOWUP_OPTION_YES'),
                    new TextModel(ConfigConstants.optionYes, yesCode.code),
                ),
                new DisplayDecorator(
                    <string>translate.instant('CONFIRMATION_FOLLOWUP_OPTION_NO'),
                    new TextModel(ConfigConstants.optionNo, noCode.code),
                ),
                new DisplayDecorator(
                    <string>translate.instant('CONFIRMATION_FOLLOWUP_OPTION_NA'),
                    new TextModel(ConfigConstants.optionNA, ConfigConstants.optionValueNA),
                ),
            ],
            new RadioModelOptions({ initialSelection: 2, invalidSelection: 2 }),
        );
        this.updateMedPhysicsEvaluationVisibility();
    }

    /**
     * Re-evaluated whenever the consumed materials change, as those decide it for service notifications.
     */
    public updateMedPhysicsEvaluationVisibility(): void {
        if (!isDefined(this.medPhysicsEvaluation)) {
            return;
        }
        const isRequired = this.isMedPhysicsEvaluationRequired();
        if (!isRequired) {
            this.medPhysicsEvaluation.selectByPredicate((entry) => entry.value === ConfigConstants.optionValueNA);
        }
        this.showMedPhysicsEvaluation = isRequired;
    }

    private isMedPhysicsEvaluationRequired(): boolean {
        const stateCode = this.notificationUtilities.getUSStateCode(this.notification);
        if (!ConfigConstants.medPhysicsUSStates.includes(stateCode)) {
            return false;
        }
        switch (this.notificationType) {
            case ConfigConstants.NotificationTypes.Installation:
                return this.notificationUtilities.hasActivityCodeGroup(
                    this.notification,
                    ConfigConstants.medPhysicsInstallationCodeGroups,
                );
            case ConfigConstants.NotificationTypes.Service:
                return (this.materialsList ?? []).some(
                    (material) =>
                        material.isMedPhysicsRelevant &&
                        material.consumptionCode?.selectedEntry?.value === CONSUME_CODE.CONSUMED,
                );
            default:
                return false;
        }
    }

    private getMedPhysicsEvaluationCrmCode(): CrmCodeModel | undefined {
        if (!this.showMedPhysicsEvaluation) {
            return undefined;
        }
        const selectedCode = this.medPhysicsEvaluation?.getSelectedValue();
        const valueHelper = this.medPhysicsEvaluationCodes.find((code) => code.code === selectedCode);
        if (!isDefined(valueHelper)) {
            return undefined;
        }
        return new CrmCodeModel({
            codeType: 'M',
            codeGroup: valueHelper.codeLevel,
            code: valueHelper.code,
            codeText: valueHelper.descText,
        });
    }

    private updateNextWorkingDayDateTime(): moment.Moment {
        const defaultStartTime = this.configService.getDefaultFollowUpTimeP58();
        const defaultFollowUpTime = moment(defaultStartTime, ConfigConstants.formatNumberPatternTime);
        // Set appointment date to tomorrow - weekday (excluding saturday and sunday)
        return TimeUtilities.getNextWorkingDayDateTime(
            defaultFollowUpTime,
            this.configService.getFollowUpDefaultDays(),
        );
    }

    public getShortConfirmationDateTime(): moment.Moment {
        return moment()
            .add(1, 'days')
            .hour(this.shortCrmFollowupDateTime.hour())
            .minute(this.shortCrmFollowupDateTime.minute())
            .startOf('minute');
    }

    /**
     * Called from markup to reset suggested appointment date and time
     * If "start immediately" checkbox is checked, it should display current date and time and cannot be edited
     * If unchecked, then display default date and time and can be changed by user
     */
    public onChangeStartImmediately(): void {
        if (this.startImmediately?.model.value) {
            const now = moment().startOf('minute');
            this.appointmentDate.value = now;
            this.appointmentTime.value = now;
        } else {
            this.appointmentDate.value = this.updateNextWorkingDayDateTime();
            this.appointmentTime.value = this.updateNextWorkingDayDateTime();
        }

        this.appointmentTime = assignModelOptions(
            this.appointmentTime,
            {
                ...this.appointmentTime.options,
                readonly: !!this.startImmediately?.model.value,
            },
            this,
        );
    }

    public determineFollowupType(): void {
        const showAppBooking = this.appBookingOptions.getSelectedValue() === ConfigConstants.optionValueYes;
        const isStartImmediately = this.configService.getShowStartImmediatelyFollowUp()
            ? this.startImmediately?.model.value
            : false;

        this.appointmentDate = assignModelOptions(
            this.appointmentDate,
            { ...this.appointmentDate.options, readonly: showAppBooking },
            this,
        );
        this.appointmentTime = assignModelOptions(
            this.appointmentTime,
            { ...this.appointmentTime.options, readonly: showAppBooking },
            this,
        );

        if (isStartImmediately) {
            this.onChangeStartImmediately();
        }
    }

    public setShortConfirmationDependency(): void {
        /**
         * For P58
         * In case of short confirmation the fields KD_DATUM, KD_ZEIT and DISP_REL has to be sent from mobile to SAP. PLAN_STD will not be sent.
         * KD_DATUM = next day.
         * KD_ZEIT = start time defined in CODGRP table.
         */
        this.followupOptions.selectByPredicate((entry) => entry.value === ConfigConstants.optionValueYes);
        this.appointmentDate.value.set(this.getShortConfirmationDateTime().toObject()); // no n eed to set appointmentTime as both date and time values share same moment object
    }

    public hasEquipmentDependencyP58(): boolean {
        return (
            isDefined(this.equipmentStatus?.equipmentAfterFix?.value) &&
            this.equipmentStatus?.equipmentAfterFix?.value !== ConfigConstants.EQUIPMENT_STATUS.WORKING
        );
    }

    public getMaterialDependencies(): MaterialModel[] {
        if (typeof this.materialsList === 'undefined') {
            return [];
        }
        return this.materialsList.filter((material) => {
            // If a material is defined as "Left On Notification" a follow-up should be forced
            if (material.consumptionCode?.selectedEntry?.value === CONSUME_CODE.LEFT_ON_NOTIFICATION) {
                return true;
            }
            // If a material is defined as "Not Received" a follow-up should be forced
            // https://evorait.atlassian.net/browse/SHCNGMAAP2-75(AA)
            return material.materialStatus?.isNotReceived;
        });
    }

    public determineFollowupDependency(): void {
        this.followUpText = [];

        const isEquipmentDependentP58 = this.hasEquipmentDependencyP58();

        const materialDependencies = this.getMaterialDependencies();

        const isInstallationPaperworkDependent = this.isInstallationPaperworkFollowupRequired();

        if (isEquipmentDependentP58) {
            this.followUpText.push(`System ${this.equipmentStatus?.equipmentAfterFix?.displayName}`);
        }
        if (materialDependencies.length > 0) {
            materialDependencies.forEach((material) =>
                this.followUpText.push(
                    `Material ${material.consumptionCode.selectedEntry.displayName} ${material.materialIdentifier.materialNumber.value} ${material.description.value}`,
                ),
            );
        }
        if (isInstallationPaperworkDependent) {
            this.followUpText.push(`InstallationPaperwork: ${this.installationPaperworkShortText}`);
        }

        if (isInstallationPaperworkDependent) {
            this.setFollowUpDependency(this.followUpText.join('\n'), this.installationPaperworkShortText);
        } else if (isEquipmentDependentP58 || materialDependencies.length > 0 || this.isMaterialRequestDependent) {
            const followUpTextShort = `${ConfigConstants.FOLLOWUP_SHORT_TEXT.FOLLOWUP}${
                isEquipmentDependentP58 ? ConfigConstants.FOLLOWUP_SHORT_TEXT.EQUIPMENT : ''
            }${materialDependencies.length > 0 ? ConfigConstants.FOLLOWUP_SHORT_TEXT.MATERIAL : ''}`;
            this.setFollowUpDependency(this.followUpText.join('\n'), followUpTextShort);
        } else {
            this.resetFollowUpDependency();
        }
    }

    public setFollowUpForInstallationPaperwork(followUpShortText: ShortTextOptionsForInstallationPaperwork): void {
        this.installationPaperworkShortText = followUpShortText;
        // delegate to determineFollowupDependency to evaluated
        // Equipment and material dependencies together with IP.
        this.determineFollowupDependency();
    }

    private isInstallationPaperworkFollowupRequired(): boolean {
        return this.installationPaperworkShortText !== ShortTextOptionsForInstallationPaperwork.FOLLOW_UP_EMPTY;
    }

    /**
     * This function deals with the dependency of followup
     *
     * @param followUpTextLong follow up long text
     * @param followupTextShort follow up short text
     */
    public setFollowUpDependency(followUpTextLong = '', followupTextShort = ''): void {
        this.followupOptions.selectByPredicate((entry) => entry.value === ConfigConstants.optionValueYes);
        this.followupOptions = assignModelOptions(
            this.followupOptions,
            {
                ...this.followupOptions.options,
                readonly: true,
            },
            this,
        );
        this.isForced = true;
        if (this.followupTextLong) {
            this.followupTextLong = new MultilineTextModel(
                this.followupTextLong.name,
                followUpTextLong,
                this.followupTextLong.options,
            );
        }
        if (this.followupTextShort) {
            this.followupTextShort = <MultilineTextModel>(
                this.set(
                    new MultilineTextModel(
                        this.followupTextShort.name,
                        followupTextShort,
                        this.followupTextShort.options,
                    ),
                )
            );
        }
        if (this.additionalInfo) {
            this.additionalInfo = assignModelOptions(
                this.additionalInfo,
                {
                    ...this.additionalInfo.options,
                    required: false,
                },
                this,
            );
        }
    }

    public resetFollowUpDependency(): void {
        // if t he followup value currently is currently not enforced, we do not reset the control since we do not want
        // to o verwrite values entered by the user. We only proceed if dependency was previously enforced by a
        // depe ndency, eg. equipment or material related.
        if (!this.isForced) {
            return;
        }

        this.isForced = false;
        this.followUpText = [];
        this.followupOptions.selectByPredicate((entry) => entry.value === ConfigConstants.optionValueNA);
        this.followupOptions = assignModelOptions(
            this.followupOptions,
            {
                ...this.followupOptions.options,
                readonly: false, // re-enable the control which was disabled by the forced dependency
            },
            this,
        );
        if (this.followupTextLong) {
            this.followupTextLong = new MultilineTextModel(
                this.followupTextLong.name,
                '',
                this.followupTextLong.options,
            );
        }
        if (this.followupTextShort) {
            this.followupTextShort = <MultilineTextModel>(
                this.set(new MultilineTextModel(this.followupTextShort.name, '', this.followupTextShort.options))
            );
        }
        if (this.additionalInfo) {
            this.additionalInfo = assignModelOptions(
                this.additionalInfo,
                {
                    ...this.additionalInfo.options,
                    required: true,
                },
                this,
            );
        }
    }

    public setFollowupFieldsToReadonly(): void {
        this.isAppointmentConfirmed = true;
        this.followupOptions = assignModelOptions(
            this.followupOptions,
            { ...this.followupOptions.options, readonly: true },
            this,
        );
        this.appBookingOptions = assignModelOptions(
            this.appBookingOptions,
            { ...this.appBookingOptions.options, readonly: true },
            this,
        );
        this.appointmentDate = assignModelOptions(
            this.appointmentDate,
            { ...this.appointmentDate.options, readonly: true },
            this,
        );
        this.appointmentTime = assignModelOptions(
            this.appointmentTime,
            { ...this.appointmentTime.options, readonly: true },
            this,
        );
        this.additionalInfo = assignModelOptions(
            this.additionalInfo,
            { ...this.additionalInfo.options, readonly: true, required: false },
            this,
        );
    }

    /**
     * Only save those value that might have been modified by the user and cannot be restored from some kind of default value
     *
     * @returns unknown object
     */
    public toJSON(): unknown {
        if (this.confirmationType === CONFIRMATION_TYPES.NORMAL) {
            this.normalCrmAppointmentTime = this.appointmentTime.value;
            this.normalCrmFollowupOptions = this.followupOptions.selectedEntry.model.value;
        }
        if (this.confirmationType === CONFIRMATION_TYPES.SHORT) {
            this.shortCrmFollowupOptions = this.followupOptions.selectedEntry.model.value;
        }
        return {
            followupOptions: this.normalCrmFollowupOptions,
            appointmentTime: this.normalCrmAppointmentTime,
            additionalTime: this.additionalTime,
            followUpReasons:
                this.followUpReasons && this.followUpReasons.selectedEntry
                    ? this.followUpReasons.selectedEntry.value
                    : undefined,
            followUpUMRCodes:
                this.followUpUMRCodes && this.followUpUMRCodes.selectedEntry
                    ? this.followUpUMRCodes.selectedEntry.value
                    : undefined,
            isMaterialDependent: this.isForced,
            followupTextLong: this.followupTextLong ? this.followupTextLong.value : undefined,
            followupTextShort: this.followupTextShort ? this.followupTextShort.value : undefined,
            additionalInfo: this.additionalInfo ? this.additionalInfo.value : undefined,
            shortCrmFollowupOptions: this.shortCrmFollowupOptions,
            remoteWork: this.remoteWork ? this.remoteWork.model.value : this.remoteWorkValue,
            startImmediately: !!this.startImmediately?.model.value,
            reasonsCode: this.reasonsCode
                ? this.reasonsCode.selectedEntries.map(
                      (node) => `${node.id}${ConfigConstants.stringSeparator}${node.value}`,
                  )
                : undefined,
            reasonsCodeFallback: this.reasonsCodeFallback
                ? this.reasonsCodeFallback.selectedEntries.map((entry) => entry.value)
                : undefined,
            assayNumber: this.assayNumber ? this.assayNumber.value : undefined,
            lotNumber: this.lotNumber ? this.lotNumber.value : undefined,
            legalCheckHandoverSafetyInformation: this.legalCheckHandoverSafetyInformation
                ? this.legalCheckHandoverSafetyInformation.selectedEntry.value
                : undefined,
            legalCheckHandoverUserDocumentation: this.legalCheckHandoverUserDocumentation
                ? this.legalCheckHandoverUserDocumentation.selectedEntry.value
                : undefined,
            appBookingOptionValue: this.appBookingOptions?.getSelectedValue(),
            appBookingDuration: this.appBookingDuration,
            isAppointmentConfirmed: this.isAppointmentConfirmed,
            installationPaperworkShortText: this.installationPaperworkShortText,
            medPhysicsEvaluation: this.medPhysicsEvaluation?.getSelectedValue(),
        };
    }

    public getFollowUpShortTextForOutModel(): string {
        if (!this.followupTextShort) {
            return '';
        }

        let resultText = this.followupTextShort.value || '';

        if (this.additionalInfo?.value?.length > 0) {
            if (resultText.length === 0) {
                resultText = ConfigConstants.FOLLOWUP_SHORT_TEXT.FOLLOWUP;
            }
            resultText = resultText.concat(ConfigConstants.FOLLOWUP_SHORT_TEXT.ADD_INFO);
        }
        return resultText;
    }

    public getCrmLongTextTRTESetForOutModel(): string {
        if (!isDefined(this.crmLongTextTRTESet)) {
            return '';
        }

        let resultText = '';

        // If both followup long text and additional info exists, they need to be combined to be sent with CRM
        if (this.followupTextLong?.value.length > 0) {
            resultText = this.followupTextLong.value.concat('\n');
        }
        if (this.additionalInfo?.value) {
            resultText = resultText.concat(this.additionalInfo.value);
        }

        return resultText;
    }

    /**
     * This method is required to avoid overwriting of appointment date and time while switching from
     * short confirmation to normal confirmation and vice versa
     *
     * @returns {ODataConfirmationFollowupOutModel} follow up outmodel
     */
    public getOutModel(): ODataConfirmationFollowupOutModel {
        /* Here we are modifying this model, before the outModel is generated with @{super.getOutModel()} */
        if (!this.isAppointmentConfirmed) {
            // If there is additional info added by user, Follow-up short text must contain the string "INT"
            // If appointment was already booked via sense, then no need to do this evaluation
            if (this.followupTextShort) {
                this.followupTextShort.value = this.getFollowUpShortTextForOutModel();
            }
            if (isDefined(this.crmLongTextTRTESet)) {
                this.crmLongTextTRTESet.value = this.getCrmLongTextTRTESetForOutModel();
            }
        }
        const outModel = <ODataConfirmationFollowupOutModel>super.getOutModel();

        if (this.isAppointmentConfirmed) {
            // If appointment was already booked via sense,
            // then treat this case in outModel as if the user had selected "No Follow Up"
            // https://evorait.atlassian.net/browse/SHP-9179
            outModel.FollowUpVisitFlag = ConfigConstants.FOLLOWUP_VISIT_FLAG.NO_FOLLOWUP;
            outModel.CrmLongTextSFOLSet = [];
            outModel.CrmLongTextTRTESet = [];
        } else {
            if (
                this.configService.getShowStartImmediatelyFollowUp() &&
                this.followupOptions?.selectedEntry.value === ConfigConstants.optionValueYes
            ) {
                outModel.ShortConfirmationIdent = this.startImmediately?.value
                    ? ConfigConstants.sapTrue
                    : ConfigConstants.sapFalse;
            }

            if (this.configService.getShowRemoteWork()) {
                if (this.followupOptions?.selectedEntry.value === ConfigConstants.optionValueYes) {
                    if (this.remoteWork?.value) {
                        outModel.FollowUpVisitFlag = ConfigConstants.FOLLOWUP_VISIT_FLAG.FOLLOWUP_REMOTE_WORK;
                    } else {
                        outModel.FollowUpVisitFlag = ConfigConstants.FOLLOWUP_VISIT_FLAG.FOLLOWUP;
                    }
                } else if (this.followupOptions?.selectedEntry.value === ConfigConstants.optionValueNo) {
                    outModel.FollowUpVisitFlag = ConfigConstants.FOLLOWUP_VISIT_FLAG.NO_FOLLOWUP;
                }
            }
        }

        // Saudi needs followup reason and flag, so we will not delete them
        if (this.confirmationType === CONFIRMATION_TYPES.SHORT) {
            delete (outModel as Omit<ODataConfirmationFollowupOutModel, 'UmrCode'>).UmrCode;
            delete (outModel as Omit<ODataConfirmationFollowupOutModel, 'FollowUpVisitAddTime'>).FollowUpVisitAddTime;
            delete (outModel as Omit<ODataConfirmationFollowupOutModel, 'CrmLongTextTRTESet'>).CrmLongTextTRTESet;
            delete (outModel as Omit<ODataConfirmationFollowupOutModel, 'CrmLongTextSFOLSet'>).CrmLongTextSFOLSet; // TODO: clear if we need for installation paperwork
            delete (outModel as Omit<ODataConfirmationFollowupOutModel, 'FollowUpVisitFlag'>).FollowUpVisitFlag;
            delete (outModel as Omit<ODataConfirmationFollowupOutModel, 'FollowupReason'>).FollowupReason;
            // For short confirmation, Follow up section hasn't been displayed to user, and if ShowStartImmediatelyFollowUp Config flag is true then it gets set to false,
            // so we're resetting it to true here to avoid sending false to SAP. This is because SAP expects this field to be true for short confirmation.
            outModel.ShortConfirmationIdent = ConfigConstants.sapTrue;
        }

        if (this.configService.getDisplayTrendCodeInFollowup()) {
            // if a reason code was selected, include its error code and error code group. Else send empty strings instead.
            let causeCode = new TextModel('CauseCode').getOutModel();
            let causeCodeGroup = new TextModel('CauseCodeGroup').getOutModel();
            if (this.reasonsCode && this.reasonsCode.selectedEntry) {
                causeCode = this.reasonsCode.selectedEntry.payload.codeName.getOutModel();
                causeCodeGroup = this.reasonsCode.selectedEntry.payload.codeGroup.getOutModel();
            } else if (this.reasonsCodeFallback && this.reasonsCodeFallback.selectedEntry) {
                causeCode = <ODataTextOutModel>this.reasonsCodeFallback.selectedEntry.getOutModel();
            }
            _.merge(outModel, causeCode);
            _.merge(outModel, causeCodeGroup);
            // If t he error code is an assay number we need to transmit assay code and lot nr as well
            if (
                this.reasonsCode &&
                this.reasonsCode.selectedEntry &&
                this.reasonsCode.selectedEntry.payload.codeGroup.value.indexOf(this.assayIndicator) === 0
            ) {
                _.merge(outModel, this.assayNumber.getOutModel());
                _.merge(outModel, this.lotNumber.getOutModel());
            }
        }

        // Built fresh on every call rather than registered as a child via `set`, so a code whose question
        // has since been hidden cannot linger in later out models
        const crmCodes: CrmCodeModel[] = [];
        if (this.showLegalCheckHandover) {
            const codeTextForYes = this.notificationLanguage === ConfigConstants.configKeyTurkey ? 'evet' : 'Yes';
            const codeTextForNo = this.notificationLanguage === ConfigConstants.configKeyTurkey ? 'hayır' : 'No';
            crmCodes.push(
                new CrmCodeModel({
                    codeType: 'M',
                    codeGroup: 'IEC6',
                    code: this.legalCheckHandoverSafetyInformation.name,
                    codeText:
                        this.legalCheckHandoverSafetyInformation.selectedEntry.value === ConfigConstants.optionValueYes
                            ? codeTextForYes
                            : codeTextForNo,
                }),
            );
            crmCodes.push(
                new CrmCodeModel({
                    codeType: 'M',
                    codeGroup: 'IEC6',
                    code: this.legalCheckHandoverUserDocumentation.name,
                    codeText:
                        this.legalCheckHandoverUserDocumentation.selectedEntry.value === ConfigConstants.optionValueYes
                            ? codeTextForYes
                            : codeTextForNo,
                }),
            );
        }
        const medPhysicsEvaluationCode = this.getMedPhysicsEvaluationCrmCode();
        if (isDefined(medPhysicsEvaluationCode)) {
            crmCodes.push(medPhysicsEvaluationCode);
        }
        if (crmCodes.length > 0) {
            _.merge(outModel, new ArrayModel<CrmCodeModel>('CrmCodesSet', crmCodes).getOutModel());
        }

        return outModel;
    }
}

export class ConfirmationFollowupModelFromJson extends ConfirmationFollowupModel {
    public constructor(
        data: JsonConfirmationFollowupModel,
        notification: NotificationModel,
        notificationType: ConfigConstants.NotificationTypes,
        crmType: CONFIRMATION_TYPES,
        isMaterialRequestDependent: boolean,
        equipmentStatus: EquipmentStatus | undefined,
        materialList: MaterialModel[] | undefined,
        equipment: JsonConfirmationEquipmentModel | undefined, // migr ation logic SHP-3884
    ) {
        super(notification, notificationType, crmType, isMaterialRequestDependent, equipmentStatus, materialList);
        if (this.confirmationType === CONFIRMATION_TYPES.NORMAL && data.followupOptions) {
            this.followupOptions.selectByPredicate((entry) => entry.value === data.followupOptions);
        }
        if (this.confirmationType === CONFIRMATION_TYPES.SHORT && data.shortCrmFollowupOptions) {
            this.followupOptions.selectByPredicate((entry) => entry.value === data.shortCrmFollowupOptions);
        }
        this.normalCrmFollowupOptions = data.followupOptions;
        this.shortCrmFollowupOptions = data.shortCrmFollowupOptions;
        if (this.remoteWork) {
            this.remoteWork.model.value = data.remoteWork;
        }
        this.remoteWorkValue = data.remoteWork;

        let followupTime = moment.invalid();
        if (this.confirmationType === CONFIRMATION_TYPES.NORMAL) {
            followupTime =
                typeof data.appointmentTime !== 'undefined' ? moment(data.appointmentTime) : this.appointmentTime.value;
        }
        if (this.confirmationType === CONFIRMATION_TYPES.SHORT) {
            this.shortCrmAppointmentTime = this.getShortConfirmationDateTime();
            followupTime = this.shortCrmAppointmentTime;
        }
        this.appointmentDate.value = followupTime;
        this.appointmentTime.value = followupTime;

        // check after init of appointmentTime if it needs be disabled dependent on startImmediately checkbox
        if (isDefined(this.startImmediately) && data.startImmediately) {
            this.startImmediately.model.value = data.startImmediately;
            this.onChangeStartImmediately();
        }

        this.normalCrmAppointmentTime = moment(data.appointmentTime);

        this.additionalTime.value = moment.duration(data.additionalTime.value);
        if (this.followUpReasons && data.followUpReasons) {
            this.followUpReasons.selectByPredicate((entry) => entry.value === data.followUpReasons);
        }
        if (this.followUpUMRCodes && data.followUpUMRCodes) {
            this.followUpUMRCodes.selectByPredicate((entry) => entry.value === data.followUpUMRCodes);
        }
        if (this.followupTextLong && data.followupTextLong) {
            this.followupTextLong.value = data.followupTextLong;
        }
        if (this.followupTextShort && data.followupTextShort) {
            this.followupTextShort.value = data.followupTextShort;
        }
        if (this.additionalInfo && data.additionalInfo) {
            this.additionalInfo.value = data.additionalInfo;
        }
        this.isForced = data.isMaterialDependent;
        if (this.isForced || isMaterialRequestDependent) {
            this.setFollowUpDependency(data.followupTextLong, data.followupTextShort);
        }
        this.installationPaperworkShortText =
            data.installationPaperworkShortText ?? ShortTextOptionsForInstallationPaperwork.FOLLOW_UP_EMPTY;

        const reasonsCode = data.reasonsCode || equipment?.reasonsCode;
        if (reasonsCode && this.reasonsCode) {
            reasonsCode.forEach((label) => {
                const nodeId = label.split(ConfigConstants.stringSeparator)[0];
                const nodeTitle = label.split(ConfigConstants.stringSeparator)[1];
                if (this.reasonsCode) {
                    this.reasonsCode.selectEntryByIdAndTitle(nodeId, nodeTitle);
                } else {
                    throw new Error(
                        'Could not re-create confirmation followup model from JSON due to missing matching reasonscode entry!',
                    );
                }
            });
        }

        const reasonsCodeFallback = data.reasonsCodeFallback || equipment?.reasonsCodeFallback;
        if (reasonsCodeFallback && this.reasonsCodeFallback) {
            reasonsCodeFallback.forEach((value) => {
                this.reasonsCodeFallback.selectByPredicate((code) => code.value === value);
            });
        }

        const assayNumber = data.assayNumber || equipment?.assayNumber;
        if (assayNumber && this.assayNumber) {
            this.assayNumber.value = assayNumber;
        }

        const lotNumber = data.lotNumber || equipment?.lotNumber;
        if (lotNumber && this.lotNumber) {
            this.lotNumber.value = lotNumber;
        }

        if (this.legalCheckHandoverUserDocumentation && data.legalCheckHandoverUserDocumentation) {
            this.legalCheckHandoverUserDocumentation.selectByPredicate(
                (entry) => entry.value === data.legalCheckHandoverUserDocumentation,
            );
        }

        if (this.legalCheckHandoverSafetyInformation && data.legalCheckHandoverSafetyInformation) {
            this.legalCheckHandoverSafetyInformation.selectByPredicate(
                (entry) => entry.value === data.legalCheckHandoverSafetyInformation,
            );
        }

        this.initializeAppBookingFieldsFromJSON(data);

        // Restored even while hidden: for service notifications the consumed materials that decide visibility
        // may arrive only after this model is rebuilt, and a hidden answer is never sent anyway
        if (isDefined(data.medPhysicsEvaluation)) {
            this.medPhysicsEvaluation?.selectByPredicate((entry) => entry.value === data.medPhysicsEvaluation);
        }
    }

    private initializeAppBookingFieldsFromJSON(data: JsonConfirmationFollowupModel): void {
        if (!this.isAppBookingEnabled) {
            return;
        }

        if (data.appBookingOptionValue) {
            this.appBookingOptions.selectByPredicate((entry) => entry.value === data.appBookingOptionValue);
        }
        if (data.appBookingDuration) {
            this.appBookingDuration = data.appBookingDuration;
        }
        this.isAppointmentConfirmed = data.isAppointmentConfirmed;
        if (this.isAppointmentConfirmed) {
            this.setFollowupFieldsToReadonly();
        }
    }
}

export interface JsonConfirmationFollowupModel {
    followupOptions: string;
    appointmentTime: string;
    additionalTime: JsonDurationModel;
    followUpReasons: string;
    followUpUMRCodes: string;
    isMaterialDependent: boolean;
    followupTextLong: string | undefined;
    followupTextShort: string | undefined;
    additionalInfo: string | undefined;
    shortCrmFollowupOptions: string | undefined;
    reasonsCode: string[] | undefined;
    reasonsCodeFallback: string[] | undefined;
    assayNumber: string | undefined;
    lotNumber: string | undefined;
    legalCheckHandoverSafetyInformation: string | undefined;
    legalCheckHandoverUserDocumentation: string | undefined;
    remoteWork: boolean;
    startImmediately: boolean;
    appBookingOptionValue: string;
    appBookingDuration: number;
    isAppointmentConfirmed: boolean;
    installationPaperworkShortText: ShortTextOptionsForInstallationPaperwork | undefined;
    medPhysicsEvaluation?: string;
}

export interface ODataConfirmationFollowupOutModel extends ODataObject {
    FollowUpVisitFlag: string;
    FollowUpVisitDate: string;
    FollowUpVisitTime: string;
    FollowUpVisitAddTime: string;
    FollowupReason: string;
    UmrCode: string;
    CrmLongTextTRTESet: ODataTextLineOutModel[];
    CrmLongTextSFOLSet: ODataTextLineOutModel[];
    CauseCode: string;
    CauseCodeGroup: string;
    AssayNumber: string;
    LotNumber: string;
    CrmCodesSet: ODataCrmCodeOutModel[];
    ShortConfirmationIdent: string;
}
